import { incApi } from 'api/inc-api';
import { useEffect, useMemo, useRef } from 'react';
import { NetworkError } from 'util/errors';

export const useCombineQueries = (...queries) => {
  const isLoading = useMemo(
    () => queries.some(query => query.isLoading),
    [queries],
  );

  const isFetching = useMemo(
    () => queries.some(query => query.isFetching),
    [queries],
  );

  const isUninitialized = useMemo(
    () => queries.some(query => query.isUninitialized),
    [queries],
  );

  const isSuccess = useMemo(
    () => queries.every(query => query.isSuccess),
    [queries],
  );

  const isError = useMemo(
    () => queries.some(query => query.isError),
    [queries],
  );

  const data = useMemo(() => queries.map(query => query.data), [queries]);

  const error = useMemo(() => queries.map(query => query.error), [queries]);

  const endpointNames = useMemo(
    () => queries.map(query => query.endpointName),
    [queries],
  );

  const originalArgs = useMemo(
    () => queries.map(query => query.originalArgs),
    [queries],
  );

  const fulfilledTimeStamp = useMemo(
    () =>
      queries
        .map(({ fulfilledTimeStamp }) => fulfilledTimeStamp)
        .sort((a, b) => b - a)[0],
    [queries],
  );

  return {
    isLoading,
    isFetching,
    isUninitialized,
    isSuccess,
    isError,
    data,
    error,
    endpointNames,
    originalArgs,
    fulfilledTimeStamp,
  };
};

export const useSuspendQueries = (...queriesArg) => {
  const throwErrors = queriesArg[queriesArg.length - 1].throwErrors;
  const options = {
    throwErrors: throwErrors ?? true,
  };

  let queries;
  if (typeof throwErrors !== 'undefined') {
    queries = queriesArg.slice(0, -1);
  } else {
    queries = queriesArg;
  }

  const combinedQueries = useCombineQueries(...queries);
  const promise = useRef();

  if (combinedQueries.isSuccess) return combinedQueries;
  else if (combinedQueries.isError) {
    if (options.throwErrors) {
      const err = combinedQueries.error.find(e => !!e);
      throw new NetworkError(err);
    }
  } else if (queries.every(query => !!query.endpointName)) {
    if (!promise.current) {
      promise.current = Promise.all(
        queries.map(({ endpointName, originalArgs }) =>
          incApi.util.getRunningOperationPromise(endpointName, originalArgs),
        ),
      );
    }

    throw promise.current;
  }

  return combinedQueries;
};

export const useQueryWithForm = (
  form,
  queryResult,
  schema,
  castOptions = {},
  transformFn = data => data,
) => {
  const { fulfilledTimeStamp, isSuccess, data } = queryResult;
  const fulfilledTimeStampRef = useRef(fulfilledTimeStamp);

  useEffect(() => {
    if (fulfilledTimeStampRef.current !== fulfilledTimeStamp && isSuccess) {
      fulfilledTimeStampRef.current = fulfilledTimeStamp;
      form.reset(schema.cast(transformFn(data), castOptions));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fulfilledTimeStamp]);
};

export const useQueryWithStepForm = ({
  form,
  queryResult,
  schema,
  editValues,
  castOptions = {},
  triggerOnLoad = false,
}) => {
  const { isSuccess } = queryResult;

  useEffect(() => {
    if (isSuccess) {
      form.reset(schema.cast(editValues, castOptions));
      if (triggerOnLoad) form.trigger();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);
};
